<template>
    <div>
        <div class="mb-3">
            <div class="d-flex align-items-center">
                <div class="fw-bold">
                    {{ currentStatus }}
                </div>
            </div>
            <div class="ms-3">
                <div>
                    <span v-bind:class="getStatusDisplayClass(charge.Status)">
                        {{ charge.StatusDisplayName }}
                    </span>
                </div>
                <div v-if="failureReasonText">
                    <span>
                        {{ failureReason }}
                    </span>
                    <span>
                        {{ failureReasonText }}
                    </span>
                </div>
                <div>
                    <span>
                        {{ changedBy }}
                    </span>
                    <span>
                        {{ getUserDisplay(charge.UserLogin) }}
                    </span>
                </div>
                <div>
                    {{ charge.DateTime }}
                </div>
            </div>
        </div>
        <div
            v-if="showExternalId"
            class="mb-3">
            <span class="fw-bold">{{ externalId }}</span>
            <span v-if="charge.ExternalChargeId">
                <span v-if="showExternalIdAsLink">
                    <a
                        v-bind:href="externalLink"
                        target="_blank">
                        {{ charge.ExternalChargeId }}
                    </a>
                </span>
                <span v-if="showExternalIdAsText">
                    {{ charge.ExternalChargeId }}
                </span>
            </span>
            <span
                v-else
                class="text-body-secondary fst-italic"
                >{{ noExternalId }}</span
            >
        </div>
        <div
            v-if="hasMetadata"
            v-bind:class="backgroundClass"
            class="card mb-3">
            <div class="card-header fw-bold">
                {{ savedMetadata }}
            </div>
            <div class="card-body">
                <pc-table
                    id="PaymentMetadata"
                    v-bind:columns="metadataColumns"
                    v-bind:items="metadata"
                    v-bind:initial-sort="[[0, 'asc']]"
                    key-prop="Name"
                    v-bind:empty-text="noMetadata"
                    class="mb-3"
                    hide-search
                    card-show-empty-placeholder />
            </div>
        </div>
        <div
            v-bind:class="backgroundClass"
            class="card">
            <div class="card-header fw-bold">
                {{ paymentStatusHistory }}
            </div>
            <div class="card-body">
                <pc-table
                    id="PaymentStatusHistory"
                    v-bind:columns="historyColumns"
                    v-bind:items="history"
                    v-bind:initial-sort="[[1, 'desc']]"
                    key-prop="TimeTicks"
                    v-bind:empty-text="noPaymentHistory"
                    hide-search>
                    <template v-slot:Status="chargeHistory">
                        <div>
                            <span v-bind:class="getStatusDisplayClass(chargeHistory.Status)">
                                {{ chargeHistory.StatusDisplayName }}
                            </span>
                        </div>
                    </template>
                    <template v-slot:UserLogin="chargeHistory">
                        <div>
                            {{ getUserDisplay(chargeHistory.UserLogin) }}
                        </div>
                    </template>
                </pc-table>
            </div>
        </div>
    </div>
</template>
<script>
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/paymentHistoryDetail.i18n.js";
    import { PAYMENT_PROVIDER, CHARGE_STATUS, statusDisplayClass } from "EPayments/ePayments";
    import { formatBasicUserInfoForDisplay } from "Util/clientUser";
    import { responsiveManager } from "Responsive/manager";

    export default {
        props: {
            currentPaymentProvider: { type: Number, required: true },
            charge: { type: Object, required: true },
            adapterOptions: { type: Object, required: true }
        },
        data() {
            return {};
        },
        computed: {
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            ),
            failureReasonText() {
                if (this.charge && this.charge.PaymentProviderId === PAYMENT_PROVIDER.Paymentus) {
                    if (this.charge.Status === CHARGE_STATUS.Failed) {
                        return this.charge.AdapterInfo;
                    }
                }
                return "";
            },
            historyColumns() {
                return [
                    {
                        header: this.statusColumn,
                        property: "Status",
                        orderable: true,
                        searchable: true,
                        visible: true,
                        sortValue(item) {
                            return item.StatusDisplayName;
                        }
                    },
                    {
                        header: this.dateColumn,
                        property: "DateTime",
                        orderable: true,
                        searchable: true,
                        visible: true,
                        sortValue: "TimeTicks"
                    },
                    {
                        header: this.changedByColumn,
                        property: "UserLogin",
                        orderable: true,
                        searchable: true,
                        visible: true,
                        sortValue(item) {
                            return formatBasicUserInfoForDisplay(item.UserLogin, null, true);
                        }
                    }
                ];
            },
            metadataColumns() {
                return [
                    {
                        header: this.nameColumn,
                        property: "Name",
                        orderable: true,
                        visible: true
                    },
                    {
                        header: this.valueColumn,
                        property: "Value",
                        orderable: true,
                        visible: true
                    }
                ];
            },
            isFromCurrentPaymentProvider() {
                return this.charge.PaymentProviderId === this.currentPaymentProvider;
            },
            isOutsidePayment() {
                return (
                    this.charge.Status === CHARGE_STATUS.OutsidePending ||
                    this.charge.Status === CHARGE_STATUS.OutsideSuccess ||
                    this.charge.Status === CHARGE_STATUS.OutsideRefund ||
                    this.charge.Status === CHARGE_STATUS.OutsideFailed
                );
            },
            externalLink() {
                return `${this.charge.ExternalIdLinkBaseUrl}${this.charge.ExternalChargeId}`;
            },
            showExternalId() {
                return this.adapterOptions.ShowExternalId;
            },
            showExternalIdAsLink() {
                return (
                    this.showExternalId &&
                    this.isFromCurrentPaymentProvider &&
                    this.charge.ExternalIdLinkBaseUrl &&
                    !this.isOutsidePayment
                );
            },
            showExternalIdAsText() {
                return this.showExternalId && !this.showExternalIdAsLink;
            },
            payer() {
                /* eslint-disable */
                return this.adapterOptions.UsePayerEmailInHistory
                    ? this.charge.PayerEmail
                    : this.adapterOptions.UsePayerNameInHistory
                      ? this.charge.PayerName
                      : "";
                /* eslint-enable */
            },
            metadata() {
                return this.charge.SavedMetadata;
            },
            hasMetadata() {
                return this.charge.SavedMetadata && this.charge.SavedMetadata.length > 0;
            },
            history() {
                return this.charge.History;
            },
            hasHistory() {
                return this.history && this.history.length > 0;
            },
            nameOverrides() {
                if (this.isFromCurrentPaymentProvider) {
                    return this.adapterOptions && this.adapterOptions.StatusNameOverrides;
                }
                return {};
            },
            backgroundClass() {
                return responsiveManager.md.value ? "" : "table-bg-shaded";
            }
        },
        methods: {
            getStatusDisplayClass(status) {
                return statusDisplayClass(status);
            },
            getUserDisplay(user) {
                return formatBasicUserInfoForDisplay(user, null, true);
            }
        }
    };
</script>
<style lang="scss" scoped>
    .table-bg-shaded {
        background-color: var(--bs-gray-200);
    }
</style>
